/**
 * PMS communication log (OpenDental "commlog") helpers.
 *
 * The API stores these records raw, exactly as the practice management system
 * wrote them, so every semantic below is an OpenDental enum rather than
 * something Trackable defined. The mode values were confirmed by reading notes
 * at each value against Hamblin's imported data on 2026-08-21, not assumed from
 * the OpenDental docs.
 */

export const COMM_MODE_NOTE = 0
export const COMM_MODE_EMAIL = 1
export const COMM_MODE_MAIL = 2
export const COMM_MODE_PHONE = 3
export const COMM_MODE_IN_PERSON = 4
export const COMM_MODE_TEXT = 5

export const COMM_MODE_LABELS: Record<number, string> = {
  [COMM_MODE_NOTE]: `Note`,
  [COMM_MODE_EMAIL]: `Email`,
  [COMM_MODE_MAIL]: `Mail`,
  [COMM_MODE_PHONE]: `Phone`,
  [COMM_MODE_IN_PERSON]: `In person`,
  [COMM_MODE_TEXT]: `Text`,
}

/**
 * Values 6 and 7 exist in Hamblin's data (seven records between them) with no
 * documented meaning, so anything unmapped reads as "Other" rather than blank.
 */
export const COMM_MODE_OTHER_LABEL = `Other`

export const SENT_OR_RECEIVED_NEITHER = 0
export const SENT_OR_RECEIVED_SENT = 1
export const SENT_OR_RECEIVED_RECEIVED = 2

/** `commSource` 0 is a human at the practice typing into the PMS. */
export const COMM_SOURCE_USER = 0

/**
 * Below this, collapsing a run costs the reader a click and saves no space, so
 * short runs render as ordinary entries.
 */
export const AUTOMATED_RUN_MIN_LENGTH = 3

type CommLogShape = {
  mode?: number | null
  sentOrReceived?: number | null
  commSource?: number | null
  commDate?: string | null
}

export type CommLogRow<T extends CommLogShape> =
  | { kind: `entry`; entry: T }
  | { kind: `automatedRun`; entries: T[]; oldest?: string | null; newest?: string | null }

/**
 * A record is worth reading in full when a staff member wrote it, or when it
 * came from the patient. Everything else is the practice's automated reminder
 * traffic, which on a sampled patient was 88 of 95 records.
 */
export function isHumanCommLogEntry(entry: CommLogShape): boolean {
  return entry.commSource === COMM_SOURCE_USER || entry.sentOrReceived === SENT_OR_RECEIVED_RECEIVED
}

export function commLogModeLabel(mode?: number | null): string {
  if (mode == null) return COMM_MODE_OTHER_LABEL
  return COMM_MODE_LABELS[mode] ?? COMM_MODE_OTHER_LABEL
}

/**
 * Collapses consecutive automated records into a single run so the human
 * entries carry the eye. Entries arrive newest first, so a run's `newest` is
 * its first member and `oldest` is its last.
 */
export function groupCommLogEntries<T extends CommLogShape>(entries: T[]): CommLogRow<T>[] {
  const rows: CommLogRow<T>[] = []
  let run: T[] = []

  const flushRun = () => {
    if (run.length === 0) return
    if (run.length < AUTOMATED_RUN_MIN_LENGTH) {
      for (const entry of run) rows.push({ kind: `entry`, entry })
    } else {
      rows.push({
        kind: `automatedRun`,
        entries: run,
        newest: run[0]?.commDate,
        oldest: run[run.length - 1]?.commDate,
      })
    }
    run = []
  }

  for (const entry of entries) {
    if (isHumanCommLogEntry(entry)) {
      flushRun()
      rows.push({ kind: `entry`, entry })
    } else {
      run.push(entry)
    }
  }
  flushRun()

  return rows
}

/** "9 texts, 5 emails" for a collapsed run's summary line. */
export function summarizeCommLogChannels(entries: CommLogShape[]): string {
  const counts = new Map<string, number>()
  for (const entry of entries) {
    const label = commLogModeLabel(entry.mode)
    counts.set(label, (counts.get(label) ?? 0) + 1)
  }

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => `${count} ${count === 1 ? label.toLowerCase() : `${label.toLowerCase()}s`}`)
    .join(`, `)
}

/**
 * Builds the Elasticsearch clause for every PMS patient a contact maps to. A
 * contact can carry several patientIds, so a single-term match would silently
 * drop half their history.
 */
export function commLogSearchForPatientIds(patientIds: string[]): string | undefined {
  const quoted = patientIds.filter(Boolean).map((id) => `"${id.replace(/"/g, `\\"`)}"`)
  if (quoted.length === 0) return undefined
  // Exclude entries Trackable wrote back to the PMS (they carry a sourceId). The native
  // Trackable record is shown on the timeline instead, so counting or listing the re-import
  // double-counts it (TRK-5725). Filtering here keeps the badge count, the list total, and the
  // feed consistent.
  return `patientId:(${quoted.join(` OR `)}) AND !sourceId:*`
}
